import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BoundingBox, Entity } from '../models/entity.model';

@Injectable({
  providedIn: 'root',
})
export class ViewerCanvasService {
  private renderer: Renderer2;
  private canvas?: HTMLCanvasElement;
  private image?: HTMLImageElement;
  private container?: HTMLElement;
  private boxes: BoundingBox[] = [];
  private allEntities: Entity[] = [];
  private currentPage: number = 1;
  private onBoxClick?: (entityId: number) => void;
  private imageScale: number = 1;
  private imageTranslate: [number, number] = [0, 0];
  private imageRotation: number = 0;
  private resizeTimeout?: any;
  private isInitialized: boolean = false;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  initialize(
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    container: HTMLElement,
    onBoxClick: (entityId: number) => void
  ) {
    if (this.isInitialized) {
      return;
    }

    this.canvas = canvas;
    this.image = image;
    this.container = container;
    this.onBoxClick = onBoxClick;
    this.isInitialized = true;
    this.setupListeners();
  }

  private setupListeners() {
    if (!this.canvas) return;

    this.renderer.listen(this.canvas, 'click', (e: MouseEvent) => {
      this.handleCanvasClick(e);
    });

    this.renderer.listen(window, 'resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.updateCanvasSize();
      }, 100);
    });
  }

  setEntities(entities: Entity[], currentPage: number) {
    this.allEntities = entities;
    this.currentPage = currentPage;
    this.boxes = this.convertEntitiesToBoxes(entities, currentPage);
    this.updateCanvasSize();
  }

  setPage(page: number) {
    this.currentPage = page;
    this.boxes = this.convertEntitiesToBoxes(this.allEntities, page);
    this.redraw();
  }

  setImageTransform(scale: number, translate: [number, number], rotation: number) {
    this.imageScale = scale;
    this.imageTranslate = translate;
    this.imageRotation = rotation;
  }

  updateCanvasSize() {
    if (!this.canvas || !this.image || !this.container) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.canvas || !this.image) return;

      const imgNaturalWidth = this.image.naturalWidth;
      const imgNaturalHeight = this.image.naturalHeight;
      
      if (imgNaturalWidth === 0 || imgNaturalHeight === 0) {
        return;
      }

      const containerWidth = this.container!.clientWidth;
      const containerHeight = this.container!.clientHeight;

      const scaleX = containerWidth / imgNaturalWidth;
      const scaleY = containerHeight / imgNaturalHeight;
      const scale = Math.min(scaleX, scaleY);

      const displayWidth = imgNaturalWidth * scale;
      const displayHeight = imgNaturalHeight * scale;

      this.canvas.width = displayWidth;
      this.canvas.height = displayHeight;
      this.renderer.setStyle(this.canvas, 'width', `${displayWidth}px`);
      this.renderer.setStyle(this.canvas, 'height', `${displayHeight}px`);

      const offsetTop = (containerHeight - displayHeight) / 2;
      const offsetLeft = (containerWidth - displayWidth) / 2;
      this.renderer.setStyle(this.canvas, 'top', `${offsetTop}px`);
      this.renderer.setStyle(this.canvas, 'left', `${offsetLeft}px`);

      this.redraw();
    });
  }

  private convertEntitiesToBoxes(entities: Entity[], currentPage: number): BoundingBox[] {
    const boxes: BoundingBox[] = [];
    
    entities.forEach((entity) => {
      entity.location?.forEach((loc) => {
        if (loc.page === currentPage) {
          const [x1, y1, x2, y2] = loc.box;
          boxes.push({
            id: boxes.length,
            entityId: entity.id,
            label: entity.label,
            x: x1,
            y: y1,
            width: x2 - x1,
            height: y2 - y1,
            page: loc.page,
            active: false,
          });
        }
      });
    });

    return boxes;
  }

  private redraw() {
    if (!this.canvas) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.boxes.forEach((box) => {
      this.drawBox(ctx, box);
    });
  }

  private drawBox(ctx: CanvasRenderingContext2D, box: BoundingBox) {
    if (!this.canvas) return;

    const x = box.x * this.canvas.width;
    const y = box.y * this.canvas.height;
    const width = box.width * this.canvas.width;
    const height = box.height * this.canvas.height;

    ctx.strokeStyle = box.active ? '#FFD700' : '#1E90FF';
    ctx.lineWidth = 1;
    ctx.fillStyle = box.active ? 'rgba(255, 215, 0, 0.2)' : 'rgba(30, 144, 255, 0.15)';

    ctx.fillRect(x, y, width, height);

    ctx.strokeRect(x, y, width, height);
  }

  private handleCanvasClick(event: MouseEvent) {
    if (!this.canvas || !this.onBoxClick) return;

    const rect = this.canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    for (let i = this.boxes.length - 1; i >= 0; i--) {
      const box = this.boxes[i];
      if (
        x >= box.x &&
        x <= box.x + box.width &&
        y >= box.y &&
        y <= box.y + box.height
      ) {
        this.onBoxClick(box.entityId);
        return;
      }
    }
  }

  setActiveEntity(entityId: number | null) {
    this.boxes.forEach((box) => {
      box.active = box.entityId === entityId;
    });
    this.redraw();
  }

  focusOnEntity(entityId: number): { scale: number; translate: [number, number]; page?: number } | null {
    if (!this.canvas || !this.container) return null;

    const entity = this.allEntities.find(e => e.id === entityId);
    if (!entity || !entity.location || entity.location.length === 0) return null;

    const firstLocation = entity.location[0];
    const entityPage = firstLocation.page;

    if (entityPage !== this.currentPage) {
      return { scale: 1, translate: [0, 0], page: entityPage };
    }

    const entityBoxes = this.boxes.filter((box) => box.entityId === entityId);
    if (entityBoxes.length === 0) return null;

    let minX = 1,
      minY = 1,
      maxX = 0,
      maxY = 0;

    entityBoxes.forEach((box) => {
      minX = Math.min(minX, box.x);
      minY = Math.min(minY, box.y);
      maxX = Math.max(maxX, box.x + box.width);
      maxY = Math.max(maxY, box.y + box.height);
    });

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const bboxWidth = maxX - minX;
    const bboxHeight = maxY - minY;

    const containerWidth = this.container.offsetWidth;
    const containerHeight = this.container.offsetHeight;
    const padding = 0.2;

    const scaleX = containerWidth / (bboxWidth * this.canvas.width * (1 + padding));
    const scaleY = containerHeight / (bboxHeight * this.canvas.height * (1 + padding));
    const scale = Math.min(scaleX, scaleY, 3);

    const translateX = this.canvas.width * (0.5 - centerX);
    const translateY = this.canvas.height * (0.5 - centerY);

    return {
      scale,
      translate: [translateX, translateY],
    };
  }

  destroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.boxes = [];
    this.canvas = undefined;
    this.image = undefined;
    this.container = undefined;
    this.isInitialized = false;
  }
}
